
import DialogTemplate from '@/_components/dialog/template/template.vue'
import { Days } from './utils'
import { cloneDeep, debounce } from 'lodash'
import { accessClient } from '@/apolloClient'
import {
    CREATE_ACCESS_RULE,
    DELETE_ACCESS_RULE,
    DELETE_ACCESS_RULE_INTERVAL,
    CREATE_ACCESS_RULE_INTERVAL,
    CREATE_ACCESS_RULE_INTERVAL_FOR_EXISTING_RULE,
    SET_ACCESS_RULE_NAME,
    SET_ACCESS_RULE_INTERVAL_REQUIRES_PIN,
    SET_ACCESS_RULE_INTERVAL_DAYS,
    SET_ACCESS_RULE_INTERVAL_FROM,
    SET_ACCESS_RULE_INTERVAL_TO,
} from './gql/mutations'
import SaveStatusIndicator from '@/components/saveStatusIndicator.vue'
import ConfirmationDialog from '@/views/Project/ch/components/confirmationDialog.vue'
import { openDialog } from '@/_components/dialog/dialog'

export default {
    name: 'AccessControlRuleDialog',
    components: { DialogTemplate, SaveStatusIndicator },
    props: {
        props: {
            type: Object,
            required: true,
        },
    },
    data() {
        let rule
        if (this.props.rule) {
            rule = cloneDeep(this.props.rule)
        } else {
            rule = {
                id: null,
                name: '',
                intervals: [
                    {
                        id: Date.now(),
                        days: [...Days].slice(0, 5),
                        from: '00:00',
                        to: '00:00',
                        requiresPin: false,
                    },
                ],
            }
        }

        rule.intervals = rule.intervals.map((interval) => {
            interval.days = Days.map((day) => {
                return interval.days.includes(day)
            })
            return interval
        })

        return {
            rule,
            updatedRule: null as Maybe<number>,
            isLoading: false,
            isDeleting: false,
            saveStatus: null as Maybe<'saved' | 'saving'>,
        }
    },
    computed: {
        days() {
            return Days
        },
    },
    methods: {
        async createNewInterval() {
            const newInterval = {
                id: Date.now(),
                days: Array(this.days.length - 1).fill(false),
                from: '00:00',
                to: '00:00',
                requiresPin: false,
            }
            this.rule.intervals.push(newInterval)

            if (!this.rule.id) {
                return
            }

            try {
                this.saveStatus = 'saving'
                const intervalResult = await accessClient.mutate({
                    mutation: CREATE_ACCESS_RULE_INTERVAL_FOR_EXISTING_RULE,
                    variables: {
                        ruleId: this.rule.id,
                        ...newInterval,
                        days: [],
                    },
                })
                this.updatedRule = cloneDeep(
                    intervalResult.data.createAccessInterval.query.accessRule
                )
                this.rule.intervals[this.rule.intervals.length - 1] = cloneDeep(
                    intervalResult.data.createAccessInterval.interval
                )
                this.saveStatus = 'saved'
                setTimeout(() => {
                    this.saveStatus = null
                }, 2000)
            } catch (e) {
                this.saveStatus = null
                throw e
            }
        },
        async deleteAccessRuleInterval(interval: any, intervalIndex: number) {
            if (!this.rule.id) {
                this.rule.intervals.splice(intervalIndex, 1)
                return
            }

            const selectedDays = [...Days].filter((_, index) => interval.days[index])
            let daysText = ''
            if (selectedDays.length === 7) {
                daysText = this.$t(`fe_access['rule.text_every_day']`).toString()
            } else if (selectedDays.length > 0) {
                daysText = selectedDays
                    .map(
                        (day) => `${day.substring(0, 1)}${day.substring(1, 3).toLowerCase()}` // e.g. Mon, Tue, Wed
                    )
                    .join(', ')
            }
            const confirm = await openDialog(ConfirmationDialog, {
                message: `fe_access['rule.message_delete_access_rule_interval']`,
                title: this.$t(`fe_access['rule.text_delete_access_rule_interval']`),
                name: `${interval.from} - ${interval.to}${daysText ? `, ${daysText}` : ''}`,
            })

            if (!confirm) {
                return
            }

            this.rule.intervals.splice(intervalIndex, 1)

            try {
                this.saveStatus = 'saving'
                const intervalResult = await accessClient.mutate({
                    mutation: DELETE_ACCESS_RULE_INTERVAL,
                    variables: {
                        intervalId: interval.id,
                    },
                })
                this.updatedRule = cloneDeep(intervalResult.data.deleteAccessInterval.rule)
                this.saveStatus = 'saved'
                setTimeout(() => {
                    this.saveStatus = null
                }, 2000)
            } catch (e) {
                this.saveStatus = null
                throw e
            }
        },
        async addNewRule() {
            if (this.rule.id) {
                return
            }

            this.isLoading = true
            try {
                const ruleResult = await accessClient.mutate({
                    mutation: CREATE_ACCESS_RULE,
                    variables: {
                        projectId: this.currentProjectId,
                        name: this.rule.name,
                    },
                })
                const rule = ruleResult.data?.createAccessRule?.rule

                let intervalResult = null
                for (const interval of this.rule.intervals) {
                    intervalResult = await accessClient.mutate({
                        mutation: CREATE_ACCESS_RULE_INTERVAL,
                        variables: {
                            projectId: this.currentProjectId,
                            ruleId: rule.id,
                            ...interval,
                            days: Days.filter((_, index) => interval.days[index]),
                        },
                    })
                }

                this.$emit('close', {
                    allRulesForProject:
                        intervalResult?.data?.createAccessInterval?.query?.allRulesForProject,
                })
            } finally {
                this.isLoading = false
            }
        },
        async deleteRule() {
            let confirm = await openDialog(ConfirmationDialog, {
                message: `fe_access['rule.message_delete_access_rule']`,
                title: this.$t(`fe_access['rule.text_delete_access_rule']`),
                name: this.rule.name,
            })

            if (!confirm) {
                return
            }

            try {
                this.isDeleting = true
                await accessClient.mutate({
                    mutation: DELETE_ACCESS_RULE,
                    variables: {
                        ruleId: this.rule.id,
                        projectId: this.currentProjectId,
                    },
                })
                this.$emit('close', { deletedAt: Date.now() })
            } finally {
                this.isDeleting = false
            }
        },
        async setInterval(time: 'from' | 'to', interval) {
            if (!this.rule.id) {
                return
            }

            this.saveStatus = 'saving'
            try {
                const intervalResult = await accessClient.mutate({
                    mutation:
                        time === 'from'
                            ? SET_ACCESS_RULE_INTERVAL_FROM
                            : SET_ACCESS_RULE_INTERVAL_TO,
                    variables: {
                        ruleId: this.rule.id,
                        intervalId: interval.id,
                        [time]: interval[time],
                    },
                })
                this.saveStatus = 'saved'
                setTimeout(() => {
                    this.saveStatus = null
                }, 2000)
                this.updatedRule = cloneDeep(
                    intervalResult.data[
                        time === 'from' ? 'setAccessIntervalFrom' : 'setAccessIntervalTo'
                    ].query.accessRule
                )
            } catch (e) {
                this.saveStatus = null
                throw e
            }
        },
        async setIntervalDays(interval) {
            if (!this.rule.id) {
                return
            }

            this.saveStatus = 'saving'
            try {
                const intervalResult = await accessClient.mutate({
                    mutation: SET_ACCESS_RULE_INTERVAL_DAYS,
                    variables: {
                        ruleId: this.rule.id,
                        intervalId: interval.id,
                        days: interval.days
                            .map((day, index) => (day ? Days[index] : null))
                            .filter(Boolean),
                    },
                })
                this.saveStatus = 'saved'
                setTimeout(() => {
                    this.saveStatus = null
                }, 2000)
                this.updatedRule = cloneDeep(
                    intervalResult.data.setAccessIntervalDays.query.accessRule
                )
            } catch (e) {
                this.saveStatus = null
                throw e
            }
        },
        async setIntervalRequiresPin(intervalId: number, requiresPin: boolean) {
            if (!this.rule.id) {
                return
            }

            this.saveStatus = 'saving'
            try {
                const ruleResult = await accessClient.mutate({
                    mutation: SET_ACCESS_RULE_INTERVAL_REQUIRES_PIN,
                    variables: {
                        ruleId: this.rule.id,
                        intervalId,
                        requiresPin,
                    },
                })
                this.saveStatus = 'saved'
                setTimeout(() => {
                    this.saveStatus = null
                }, 2000)
                this.updatedRule = cloneDeep(
                    ruleResult.data.setAccessIntervalRequiresPin.query.accessRule
                )
            } catch (e) {
                this.saveStatus = null
                throw e
            }
        },
        setRuleName: debounce(async function () {
            if (!this.rule.id) {
                return
            }

            this.saveStatus = 'saving'
            try {
                const ruleResult = await accessClient.mutate({
                    mutation: SET_ACCESS_RULE_NAME,
                    variables: {
                        ruleId: this.rule.id,
                        name: this.rule.name,
                    },
                })
                this.saveStatus = 'saved'
                setTimeout(() => {
                    this.saveStatus = null
                }, 2000)
                this.updatedRule = cloneDeep(ruleResult.data.setAccessRuleName.rule)
            } catch (e) {
                this.saveStatus = null
                throw e
            }
        }, 500),
    },
}
